import yn from 'yn';

import { getLogger } from '../logging';
import { StorageKeys, StorageService } from '../storage';
import { BuildConfig, Environment } from './build';
import { Constants } from './constants';

const logger = getLogger('config');

export class ConfigService {
    private _currentEnvName: string;
    private _debugEnvName: string;
    private _ready: Promise<void>;

    ready() {
        return this._ready;
    }

    get currentEnvName(): string {
        return this._currentEnvName;
    }

    get debugEnvName(): string {
        return this._debugEnvName;
    }

    get buildDebugEnabled() {
        try {
            // Safe property access with multiple fallback strategies
            let debuggerValue;

            // First try with buildEnv
            if (this.buildConfig?.buildEnv && this.buildConfig?.availableEnvironments?.[this.buildConfig.buildEnv]) {
                debuggerValue =
                    this.buildConfig.availableEnvironments[this.buildConfig.buildEnv][Constants.Env.EnableDebugger];
            }

            // Fallback to currentEnvName if buildEnv doesn't work
            if (debuggerValue === undefined && this.buildConfig?.availableEnvironments?.[this._currentEnvName]) {
                debuggerValue =
                    this.buildConfig.availableEnvironments[this._currentEnvName][Constants.Env.EnableDebugger];
            }

            // Parse the value safely
            return yn(debuggerValue, { default: false });
        } catch (error) {
            logger.error('Failed to read the buildDebugEnabled flag', {
                buildConfigPresent: !!this.buildConfig,
                buildEnv: this.buildConfig?.buildEnv,
                currentEnvName: this._currentEnvName,
                error,
            });

            // Safe fallback - assume debug is disabled if we can't determine it
            return false;
        }
    }

    get env(): Environment {
        try {
            const environment = this.buildConfig.availableEnvironments[this._currentEnvName];

            if (!environment) {
                const available = Object.keys(this.buildConfig.availableEnvironments || {});
                logger.warn(
                    `Environment [${this._currentEnvName}] not found in availableEnvironments [${available.join(', ')}]`,
                    { available, currentEnvName: this._currentEnvName }
                );

                // Fallback to the first available environment
                const [firstAvailableEnv] = available;
                if (firstAvailableEnv) {
                    logger.warn(`Falling back to environment [${firstAvailableEnv}]`, {
                        fallbackEnv: firstAvailableEnv,
                    });
                    return this.buildConfig.availableEnvironments[firstAvailableEnv];
                }

                logger.error('No environments available in buildConfig');
                return {} as Environment;
            }

            return environment;
        } catch (error) {
            logger.error('Failed to access the current environment', { error });
            return {} as Environment;
        }
    }

    constructor(
        private storageService: StorageService,
        private buildConfig: BuildConfig
    ) {
        // Validate buildConfig on construction
        this.validateBuildConfig();

        this._currentEnvName = buildConfig.buildEnv || 'local';
        this._debugEnvName = buildConfig.debugEnv || 'local';

        // Always trust the build-time env on startup. changeEnv() can override during the session.
        this._ready = storageService.setItem(StorageKeys.CurrentEnv, this._currentEnvName).catch((error) => {
            logger.error('Failed to store the current environment', { error });
        });
    }

    private validateBuildConfig() {
        if (!this.buildConfig) {
            throw new Error('BuildConfig is required but was not provided');
        }

        if (!this.buildConfig.availableEnvironments || typeof this.buildConfig.availableEnvironments !== 'object') {
            throw new Error('BuildConfig.availableEnvironments is required and must be an object');
        }

        const envCount = Object.keys(this.buildConfig.availableEnvironments).length;
        if (envCount === 0) {
            throw new Error('BuildConfig.availableEnvironments cannot be empty');
        }
    }

    get(key: keyof Environment, defaultValue?: string) {
        try {
            const value = this.env[key];
            if (value === undefined && defaultValue === undefined) {
                logger.warn(`Environment key [${String(key)}] not found and no default provided`, {
                    key: String(key),
                });
            }
            return value || defaultValue;
        } catch (error) {
            logger.error(`Failed to read environment key [${String(key)}]`, {
                error,
                key: String(key),
            });
            return defaultValue;
        }
    }

    // Environment variables are strings
    // this parses an env var intended as boolean
    getBoolean(key: keyof Environment, defaultValue = false) {
        try {
            // The following case-insensitive values are recognized:
            // 'on', 'y', 'yes', 'true', '1' --> true
            // 'off', 'n', 'no', 'false', '0' --> false
            const value = this.env[key];
            return yn(value, { default: defaultValue });
        } catch (error) {
            logger.error(`Failed to read boolean environment key [${String(key)}]`, {
                error,
                key: String(key),
            });
            return defaultValue;
        }
    }

    changeEnv(env: string) {
        try {
            if (!env || typeof env !== 'string') {
                throw new Error('Environment name must be a non-empty string');
            }

            const availableEnvs = Object.keys(this.buildConfig.availableEnvironments || {});
            if (!availableEnvs.includes(env)) {
                throw new Error(
                    `Unrecognized environment name: '${env}'. Available environments: ${availableEnvs.join(', ')}`
                );
            }

            logger.info(`Changing environment from [${this._currentEnvName}] to [${env}]`, {
                from: this._currentEnvName,
                to: env,
            });

            this._currentEnvName = env;
            this._debugEnvName = env;

            return this.storageService.setItem(StorageKeys.CurrentEnv, env).catch((error) => {
                logger.error('Failed to save the environment change to storage', { error });
                // Revert the change if storage fails
                this._currentEnvName = this._currentEnvName;
                this._debugEnvName = this._debugEnvName;
                throw error;
            });
        } catch (error) {
            logger.error(`Failed to change environment to [${env}]`, { error, targetEnv: env });
            throw error;
        }
    }

    // Type-safe environment key access
    private getEnvValue(key: string, defaultValue?: string): string | undefined {
        try {
            const env = this.env as Record<string, string | undefined>;
            return env[key] || defaultValue;
        } catch (error) {
            logger.error(`Failed to read environment key [${key}]`, { error, key });
            return defaultValue;
        }
    }

    // Additional helper method to get auth configuration safely
    getAuthConfig() {
        try {
            const env = this.env;

            // Use type-safe property access
            const loginUrl =
                this.getEnvValue('SAML_LOGIN_URL') || this.getEnvValue('LOGIN_URL') || this.getEnvValue('AUTH_URL');

            return {
                loginUrl: loginUrl,
                samlLoginUrl: this.getEnvValue('SAML_LOGIN_URL'),
                apiUrl: this.getEnvValue('API_URL'),
                authScheme: this.getEnvValue('AUTH_SCHEME') || 'subcomm',
                debugEnabled: this.buildDebugEnabled,
                environment: this._currentEnvName,
            };
        } catch (error) {
            logger.error('Failed to build the auth configuration', { error });
            return {
                loginUrl: null,
                samlLoginUrl: null,
                apiUrl: null,
                authScheme: 'subcomm',
                debugEnabled: false,
                environment: this._currentEnvName,
            };
        }
    }

    // Helper method to check if the service is properly configured
    isValidConfiguration(): boolean {
        try {
            const env = this.env;
            const hasRequiredKeys = env && typeof env === 'object';
            const hasLoginUrl = !!this.getEnvValue('LOGIN_URL');

            return hasRequiredKeys && hasLoginUrl;
        } catch (error) {
            logger.error('Failed to validate the configuration', { error });
            return false;
        }
    }
}
